import React, { useCallback, useMemo } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import { noop } from 'lodash-es'
import { useTheme } from 'styled-components/native'

import { CARE_OPTION } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/treatmentOptions'
import { ExtendedPathwayOption, PathwayOptions } from '@lyrahealth-inc/shared-app-logic/src/features/pathways/constants'
import { OnCareOptionModalOpenCloseProps } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/constants'

import { BodyText } from '../atoms/bodyText/BodyText'
import { Link } from '../atoms/link/Link'
import { AURORA_CARE_OPTIONS_HEADERS, AuroraCareOption } from '../careOptionsMetadata/auroraCareOptionsMetadata'
import { BodyTextSize } from '../styles/typeStyles'
import { ThemeType } from '../utils/themes/ThemeProvider'
import { tID } from '../utils/utils'

const MODAL_HEADERS = defineMessages({
  MEDICATION_CONSULTATION: {
    defaultMessage: 'Medication consultation',
    description: 'Header for modal that has information about the medication consultation offering',
  },
  // Shared headers across legacy and Aurora care options
  PARENTING_COACHING: {
    defaultMessage: 'Coaching for parents',
    description: 'Header of modal giving information about the coaching for parents program',
  },
  FAMILY_THERAPY: {
    defaultMessage: 'Family therapy',
    description: 'Header of modal giving information about family therapy',
  },
})

const MODAL_CTA_TEXT = defineMessages({
  FIND_COACH: {
    defaultMessage: 'Find a coach',
    description: 'Label of button, cta to find a parenting coach',
  },
  FIND_THERAPIST: {
    defaultMessage: 'Find a therapist',
    description: 'Label of button, cta to find a therapist',
  },
  BOOK_CONSULTATION: {
    defaultMessage: 'Book a consultation',
    description: 'Label of button, cta to book a medication consultation',
  },
  FIND_PROVIDER: {
    defaultMessage: 'Find a provider',
    description: 'Label of button, cta to find a provider',
  },
  SCHEDULE_CALL: {
    defaultMessage: 'Schedule a call',
    description: 'Label of button, cta to schedule a call',
  },
})

const CARE_OPTION_MODAL_DESCRIPTIONS = defineMessages({
  [CARE_OPTION.PARENT_COACHING]: {
    defaultMessage:
      'Meet one-on-one with a parenting coach to improve your own mental well-being and address challenging parenting situations like tantrums, navigating independence, screen time, and more.',
    description: 'Description of the parent coaching program',
  },
  [CARE_OPTION.FAMILY_THERAPY]: {
    defaultMessage:
      'Bring your family together to overcome challenges, foster healthier dynamics, or address the behavioral health needs of your young children. A therapist will work with you to identify goals, learn new communication styles, and more.',
    description: 'Description of family therapy',
  },
  [CARE_OPTION.MEDICATION_CONSULTATION]: {
    defaultMessage:
      'Meet with a Lyra prescriber to discuss whether mental health medication is the right path for you. Whether you are looking for a first-time consultation, a second opinion on your current plan, or a refill for an existing prescription—including controlled substances like Adderall or Xanax—our providers are here to offer the professional support and guidance you need.',
    description: 'text giving an overview of medication treatment option',
  },
})

const MEDICATION_CONSULTATION_CONTENT = defineMessages({
  HEADER_FOR_DEPENDENTS: {
    defaultMessage: 'Medication consultation for {dependentType}',
    description: 'Header of modal giving information about medication consultation for a children or teens',
  },
  DESCRIPTION_FOR_DEPENDENTS: {
    defaultMessage:
      'A Lyra prescriber can help determine whether medication might be a helpful option for your {dependentType} and prescribe prescription refills if necessary.',
    description: "Description of medication consultation program for the user's child or teen",
  },
  CHILDREN: {
    defaultMessage: 'children',
    description: 'Label for children as a dependent type',
  },
  CHILD: {
    defaultMessage: 'child',
    description: 'Label for child (singular) as a dependent type',
  },
  TEENS: {
    defaultMessage: 'teens',
    description: 'Label for teens as a dependent type',
  },
  TEEN: {
    defaultMessage: 'teen',
    description: 'Label for teen (singular) as a dependent type',
  },
  INFO_TEXT: {
    defaultMessage:
      'If you’re on a controlled medication (Adderall, Xanax, etc.) and wish to continue, we can connect you to a Lyra prescriber for a consult. {hideContinueLink, select, yes {} other {<link>Continue</link>}}',
    description: 'Footnote information for access to medication when direct access meds is supported',
  },
  INFO_TEXT_NO_DIRECT_ACCESS: {
    defaultMessage:
      'If you require a prescription for controlled medications (such as Adderall or Xanax), please contact your health plan directly.',
    description: 'Footnote information for access to medication when direct access meds is not supported',
  },
  DA_MEDICATION_CONSULTATION: {
    defaultMessage:
      'Meet with a Lyra prescriber to discuss whether mental health medication is the right path for you. Whether you are looking for a first-time consultation, a second opinion on your current plan, or a refill for an existing prescription—including controlled substances like Adderall or Xanax—our providers are here to offer the professional support and guidance you need.',
    description:
      'Description of the medication consultation program when direct access meds is the only medication option',
  },
})

interface UseGetCareOptionsModalContentProps {
  selectedPathwayOption?: ExtendedPathwayOption
  userAge?: number
  onCareOptionModalOpen: (props: OnCareOptionModalOpenCloseProps) => void
  onCareOptionModalClose: (props: OnCareOptionModalOpenCloseProps) => void
  onContinuePress: () => void
  isDirectAccessMedsSupported: boolean
  isBlendedCareMedsAvailable?: boolean
  // used to reset selected pathway option so that modal is only opened on initial page load
  resetSelectedPathwayOption?: () => void
}

export const useGetCareOptionsModalContent = ({
  selectedPathwayOption,
  userAge,
  onCareOptionModalOpen,
  onCareOptionModalClose,
  onContinuePress = noop,
  isDirectAccessMedsSupported,
  isBlendedCareMedsAvailable = true,
  resetSelectedPathwayOption = noop,
}: UseGetCareOptionsModalContentProps) => {
  const isDAMedsOnly = isDirectAccessMedsSupported && !isBlendedCareMedsAvailable
  const { formatMessage } = useIntl()
  const { isAuroraTheme } = useTheme() as ThemeType
  const boldTextValue = useMemo(() => {
    return {
      b: (chunks: string) => <BodyText bold>{chunks}</BodyText>,
    }
  }, [])

  const onProgramDetailsOpenClose = useCallback(
    (open: boolean, treatmentOption: CARE_OPTION) => {
      const props = { treatmentOption, age: userAge }
      if (open) {
        onCareOptionModalOpen(props)
      } else {
        onCareOptionModalClose(props)
      }

      // clear the initial selectedPathwayOption so that modal won't open again on reload from a refresh or back navigation
      resetSelectedPathwayOption()
    },
    [onCareOptionModalClose, onCareOptionModalOpen, resetSelectedPathwayOption, userAge],
  )

  return useMemo(() => {
    return {
      [CARE_OPTION.PARENT_COACHING]: {
        isModalOpenInitially: selectedPathwayOption === PathwayOptions.PARENTING_TOOLKIT,
        ...(!isAuroraTheme && {
          headerIllustration: 'CareOptionParentingSupportIcon',
        }),
        header: formatMessage(MODAL_HEADERS.PARENTING_COACHING),
        description: formatMessage(CARE_OPTION_MODAL_DESCRIPTIONS[CARE_OPTION.PARENT_COACHING]),
        programPoints: [
          {
            icon: 'BookSessionsIcon',
            description: formatMessage(
              {
                defaultMessage: '<b>Book</b> regular 45-minute sessions at a time that works for you.',
                description: 'Details on sessions offered for parent coaching',
              },
              boldTextValue,
            ),
          },
          {
            icon: 'PracticeSkillsIcon',
            description: formatMessage(
              {
                defaultMessage: '<b>Get</b> personalized care between sessions with guided practices.',
                description: 'Details on care offered between sessions for parent coaching',
              },
              boldTextValue,
            ),
          },
          {
            icon: 'ProgressIcon',
            description: formatMessage(
              {
                defaultMessage: '<b>Track</b> your progress and reach your parenting goals with your coach’s support.',
                description: 'Details on tracking progress throughout sessions for parent coaching',
              },
              boldTextValue,
            ),
          },
        ],
        ctaText: formatMessage(MODAL_CTA_TEXT.FIND_COACH),
        onModalOpen: () => {
          onProgramDetailsOpenClose(true, CARE_OPTION.PARENT_COACHING)
        },
        onModalClose: () => {
          onProgramDetailsOpenClose(false, CARE_OPTION.PARENT_COACHING)
        },
      },
      [CARE_OPTION.FAMILY_THERAPY]: {
        isModalOpenInitially: selectedPathwayOption === PathwayOptions.FAMILY_THERAPY,
        ...(!isAuroraTheme && {
          headerIllustration: 'Family',
        }),
        header: formatMessage(MODAL_HEADERS.FAMILY_THERAPY),
        description: formatMessage(CARE_OPTION_MODAL_DESCRIPTIONS[CARE_OPTION.FAMILY_THERAPY]),
        programPoints: [
          {
            icon: 'BookSessionsIcon',
            description: formatMessage(
              {
                defaultMessage: '<b>Book</b> regular 50-minute sessions at a convenient time.',
                description: 'Details on sessions offered for family therapy',
              },
              boldTextValue,
            ),
          },
          {
            icon: 'ProgressIcon',
            description: formatMessage(
              {
                defaultMessage: '<b>Track</b> your and your family’s progress with your therapist’s support.',
                description: 'Details on tracking progress throughout sessions for family therapy',
              },
              boldTextValue,
            ),
          },
        ],
        ctaText: formatMessage(MODAL_CTA_TEXT.FIND_THERAPIST),
        onModalOpen: () => {
          onProgramDetailsOpenClose(true, CARE_OPTION.FAMILY_THERAPY)
        },
        onModalClose: () => {
          onProgramDetailsOpenClose(false, CARE_OPTION.FAMILY_THERAPY)
        },
      },
      [CARE_OPTION.MEDICATION_CONSULTATION]: {
        isModalOpenInitially: selectedPathwayOption === PathwayOptions.MEDICATION_CONSULTATION,
        ...(!isAuroraTheme && {
          headerIllustration: 'MedicationConsultationIllustration',
        }),
        header: formatMessage(MODAL_HEADERS.MEDICATION_CONSULTATION),
        description: isDAMedsOnly
          ? formatMessage(MEDICATION_CONSULTATION_CONTENT.DA_MEDICATION_CONSULTATION)
          : formatMessage(CARE_OPTION_MODAL_DESCRIPTIONS[CARE_OPTION.MEDICATION_CONSULTATION]),
        programPointsHeaderText: formatMessage({
          defaultMessage: 'We\u2019re by your side throughout the entire process:',
          description: 'Header of section which explains what to expect from the medication consultation program',
        }),
        programPoints: [
          {
            icon: 'ProgressIcon',
            description: formatMessage({
              defaultMessage: 'Symptom check-ins to mitigate side effects',
              description: 'Details on symptom tracking',
            }),
          },
          {
            icon: 'BookSessionsIcon',
            description: formatMessage({
              defaultMessage: 'Easy access to refills and follow-ups',
              description: 'Details on easy access to refills and follow-up appointments',
            }),
          },
          ...(!isDAMedsOnly
            ? [
                {
                  icon: 'InfoIcon',
                  description: isDirectAccessMedsSupported
                    ? formatMessage(MEDICATION_CONSULTATION_CONTENT.INFO_TEXT, {
                        link: (chunks: string) => (
                          <Link
                            text={chunks}
                            size={BodyTextSize.SMALL}
                            onPress={onContinuePress}
                            underline
                            testID={tID(`CareOption-medicationConsultation-continue`)}
                          />
                        ),
                        hideContinueLink: onContinuePress === noop ? 'yes' : 'other',
                      })
                    : formatMessage(MEDICATION_CONSULTATION_CONTENT.INFO_TEXT_NO_DIRECT_ACCESS),
                },
              ]
            : []),
        ],
        ctaText: formatMessage(MODAL_CTA_TEXT.BOOK_CONSULTATION),
        onModalOpen: () => {
          onProgramDetailsOpenClose(true, CARE_OPTION.MEDICATION_CONSULTATION)
        },
        onModalClose: () => {
          onProgramDetailsOpenClose(false, CARE_OPTION.MEDICATION_CONSULTATION)
        },
        centerProgramPoints: true,
      },
    }
  }, [
    boldTextValue,
    formatMessage,
    isAuroraTheme,
    isDAMedsOnly,
    isDirectAccessMedsSupported,
    onContinuePress,
    onProgramDetailsOpenClose,
    selectedPathwayOption,
  ])
}

const AURORA_CARE_OPTION_MODAL_DESCRIPTIONS = defineMessages({
  [AuroraCareOption.PARENTING_COACHING]: {
    defaultMessage:
      'Work 1-on-1 with a parenting coach to improve your own mental wellbeing, while tackling challenging parenting situations.',
    description: 'Description of the parent coaching program',
  },
  [AuroraCareOption.COUPLES_THERAPY]: {
    defaultMessage:
      'Work with a therapist to learn to communicate better, address relationship challenges, and navigate issues such as depression, substance use, or PTSD within a relationship.',
    description: 'Description of the couples therapy program',
  },
  [AuroraCareOption.FAMILY_THERAPY]: {
    defaultMessage:
      'Bring your family together and work with a therapist to overcome challenges, identify behavioral issues, foster healthier dynamics, or learn new communication styles.',
    description: 'Description of the family therapy program',
  },
  [AuroraCareOption.CONTROLLED_MEDS]: {
    defaultMessage:
      'Meet with a Lyra prescriber if you require a prescription for a controlled medication such as Adderall or Xanax.',
    description: 'Description of accessing controlled medications through Lyra',
  },

  [AuroraCareOption.MEDICATION_CONSULTATION]: {
    defaultMessage:
      'Work with a board-certified Lyra prescriber to evaluate treatment options and determine if medication for mental health might work for you. If you’re already taking a medication, see if it’s still the best fit for your needs.',
    description: 'Description of the medication consultation program',
  },
  [AuroraCareOption.AUD_SUD]: {
    defaultMessage:
      'Meet with a therapist to improve your relationship with alcohol or substance use, build healthier habits, and get access to optional group support.',
    description: 'Description of Treatment for alcohol or substance use care option',
  },
  [AuroraCareOption.DBT]: {
    defaultMessage:
      'If you are experiencing suicidal thoughts or self-harm, our Dialectical Behavioral Therapy (DBT) specialists are here to offer support. This program can help you navigate crises, cope with distressing emotions, and improve your relationships.',
    description: 'Description of the DBT program',
  },
  [AuroraCareOption.IOP]: {
    defaultMessage:
      'Members participate in a structured program several days a week virtually or at a clinic. While intensive, members are still able to participate in their daily routines.',
    description: 'Description of the IOP program',
  },
  [AuroraCareOption.PHP]: {
    defaultMessage:
      'Members participate in a daily intensive program while still living at home. Programs include individual and group therapy as well as medication management.',
    description: 'Description of the PHP program',
  },
  [AuroraCareOption.RESIDENTIAL_TREATMENT]: {
    defaultMessage:
      'Members stay at a facility to receive 24/7 care and build life skills for sustainable recovery. The program may include individual or group therapy, medication management, and therapeutic recreation activities.',
    description: 'Description of the residential treatment program',
  },
  [AuroraCareOption.INPATIENT_HOSPITALIZATION]: {
    defaultMessage:
      'Members stay at a hospital for a short period of time to safely address an urgent mental health crisis. More intensive medical treatment or stabilization can be provided. Treatment can be voluntary or involuntary.',
    description: 'Description of the inpatient hospitalization program',
  },
})

const HOW_IT_WORKS_CONTENT_SHARED = defineMessages({
  SESSION_OPTIONS: {
    defaultMessage: 'SESSION OPTIONS',
    description:
      'Text shown above the type of session options for the selected care program (in-person, by video, or both)',
  },
  SESSION_OPTIONS_IN_PERSON: {
    defaultMessage: 'In-person',
    description: 'Label to describe in-person session options for the selected care program',
  },
  SESSION_OPTIONS_BY_VIDEO: {
    defaultMessage: 'By video',
    description: 'Label to describe by video session options for the selected care program',
  },
  SESSION_OPTIONS_BOTH: {
    defaultMessage: 'In-person or by video',
    description: 'Label to describe either in-person or by video session options for the selected care program',
  },
  TREATMENT_LENGTH: {
    defaultMessage: 'TREATMENT LENGTH',
    description:
      'Text shown above the duration of treatment length for the selected care program, for example 4 months or 4 to 8 weeks',
  },
  LOCATION: {
    defaultMessage: 'LOCATION',
    description: 'Text shown above the location options for the selected care program (home, hospital, facility, etc.)',
  },
  LOCATION_HOME: {
    defaultMessage: 'Home',
    description: 'Label to describe home as a location option for the selected care program',
  },
  COMPLEX_CARE_SPECIALIST: {
    defaultMessage: 'COMPLEX CARE SPECIALIST',
    description:
      'Text shown above the description of the complex care specialist offering for the selected care program',
  },
  COMPLEX_CARE_SPECIALIST_DESCRIPTION: {
    defaultMessage: 'End-to-end support',
    description: 'Description of the complex care specialist offering for the selected care program',
  },
})

interface UseGetAuroraCareOptionsModalContentProps {
  onCareOptionModalOpen?: (careOption: AuroraCareOption) => void
  onCareOptionModalClose?: (careOption: AuroraCareOption) => void
  onContinuePress?: () => void
  isDirectAccessMedsSupported: boolean
  isBlendedCareMedsAvailable?: boolean
}

export const useGetAuroraCareOptionsModalContent = ({
  onCareOptionModalOpen = noop,
  onCareOptionModalClose = noop,
  onContinuePress = noop,
  isDirectAccessMedsSupported,
  isBlendedCareMedsAvailable = true,
}: UseGetAuroraCareOptionsModalContentProps) => {
  const isDAMedsOnly = isDirectAccessMedsSupported && !isBlendedCareMedsAvailable
  const { formatMessage } = useIntl()

  const onProgramDetailsOpenClose = useCallback(
    (open: boolean, careOption: AuroraCareOption) => {
      if (open) {
        onCareOptionModalOpen(careOption)
      } else {
        onCareOptionModalClose(careOption)
      }
    },
    [onCareOptionModalClose, onCareOptionModalOpen],
  )

  const createModalCallbacks = useCallback(
    (careOption: AuroraCareOption) => ({
      onModalOpen: () => onProgramDetailsOpenClose(true, careOption),
      onModalClose: () => onProgramDetailsOpenClose(false, careOption),
    }),
    [onProgramDetailsOpenClose],
  )

  const SHARED_PROGRAM_POINTS = useMemo(() => {
    return {
      SESSION_OPTIONS: {
        icon: 'SofaIcon',
        descriptionEyebrow: formatMessage(HOW_IT_WORKS_CONTENT_SHARED.SESSION_OPTIONS),
      },
      TREATMENT_LENGTH: {
        icon: 'BookSessionsIcon',
        descriptionEyebrow: formatMessage(HOW_IT_WORKS_CONTENT_SHARED.TREATMENT_LENGTH),
      },
      LOCATION_HOME: {
        icon: 'HomeIcon',
        descriptionEyebrow: formatMessage(HOW_IT_WORKS_CONTENT_SHARED.LOCATION),
        description: formatMessage(HOW_IT_WORKS_CONTENT_SHARED.LOCATION_HOME),
      },
      COMPLEX_CARE_SPECIALIST: {
        icon: 'HeadphoneHeartIcon',
        descriptionEyebrow: formatMessage(HOW_IT_WORKS_CONTENT_SHARED.COMPLEX_CARE_SPECIALIST),
        description: formatMessage(HOW_IT_WORKS_CONTENT_SHARED.COMPLEX_CARE_SPECIALIST_DESCRIPTION),
      },
    }
  }, [formatMessage])

  // used for medication consultation for children and teens
  const MEDICATION_CONSULTATION_DEPENDENTS_PROGRAM_POINTS = useMemo(
    () => [
      {
        icon: 'SideNavMessageIcon',
        description: formatMessage({
          defaultMessage: 'Get a comprehensive consultation to discuss treatment options.',
          description: 'List item in description of the medication consultation program for children',
        }),
      },
      {
        icon: 'BookSessionsIcon',
        description: formatMessage({
          defaultMessage: 'Check in regularly with the provider.',
          description: 'List item in description of the medication consultation program for children',
        }),
      },
    ],
    [formatMessage],
  )

  return useMemo(() => {
    return {
      [AuroraCareOption.PARENTING_COACHING]: {
        header: formatMessage(MODAL_HEADERS.PARENTING_COACHING),
        description: formatMessage(AURORA_CARE_OPTION_MODAL_DESCRIPTIONS[AuroraCareOption.PARENTING_COACHING]),
        programPoints: [
          {
            icon: 'BookSessionsIcon',
            description: formatMessage({
              defaultMessage: '45-minute sessions on your schedule',
              description: 'List item in description of the parent coaching program',
            }),
          },
          {
            icon: 'PracticeSkillsIcon',
            description: formatMessage({
              defaultMessage: 'Resources and activities between sessions',
              description: 'List item in description of the parent coaching program',
            }),
          },
          {
            icon: 'ProgressIcon',
            description: formatMessage({
              defaultMessage: 'Track progress on your parenting goals',
              description: 'List item in description of the parent coaching program',
            }),
          },
        ],
        ctaText: formatMessage(MODAL_CTA_TEXT.FIND_COACH),
        ...createModalCallbacks(AuroraCareOption.PARENTING_COACHING),
      },
      [AuroraCareOption.COUPLES_THERAPY]: {
        header: formatMessage(AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.COUPLES_THERAPY]),
        description: formatMessage(AURORA_CARE_OPTION_MODAL_DESCRIPTIONS[AuroraCareOption.COUPLES_THERAPY]),
        programPoints: [
          {
            icon: 'BookSessionsIcon',
            description: formatMessage({
              defaultMessage: '50-minute sessions on your schedule',
              description: 'List item in description of the couples therapy program',
            }),
          },
          {
            icon: 'ProgressIcon',
            description: formatMessage({
              defaultMessage: 'Track your and your partner’s progress',
              description: 'List item in description of the couples therapy program',
            }),
          },
        ],
        ctaText: formatMessage(MODAL_CTA_TEXT.FIND_THERAPIST),
        ...createModalCallbacks(AuroraCareOption.COUPLES_THERAPY),
      },
      [AuroraCareOption.FAMILY_THERAPY]: {
        header: formatMessage(MODAL_HEADERS.FAMILY_THERAPY),
        description: formatMessage(AURORA_CARE_OPTION_MODAL_DESCRIPTIONS[AuroraCareOption.FAMILY_THERAPY]),
        programPoints: [
          {
            icon: 'BookSessionsIcon',
            description: formatMessage({
              defaultMessage: '50-minute sessions on your schedule',
              description: 'List item in description of the family therapy program',
            }),
          },
          {
            icon: 'ProgressIcon',
            description: formatMessage({
              defaultMessage: 'Track your and your family’s progress',
              description: 'List item in description of the family therapy program',
            }),
          },
        ],
        ctaText: formatMessage(MODAL_CTA_TEXT.FIND_THERAPIST),
        ...createModalCallbacks(AuroraCareOption.FAMILY_THERAPY),
      },
      [AuroraCareOption.CONTROLLED_MEDS]: {
        header: formatMessage(AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.CONTROLLED_MEDS]),
        description: formatMessage(AURORA_CARE_OPTION_MODAL_DESCRIPTIONS[AuroraCareOption.CONTROLLED_MEDS]),
        ctaText: formatMessage(MODAL_CTA_TEXT.FIND_PROVIDER),
        ...createModalCallbacks(AuroraCareOption.CONTROLLED_MEDS),
      },
      [AuroraCareOption.MEDICATION_CONSULTATION]: {
        header: formatMessage(AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.MEDICATION_CONSULTATION]),
        description: isDAMedsOnly
          ? formatMessage(MEDICATION_CONSULTATION_CONTENT.DA_MEDICATION_CONSULTATION)
          : formatMessage(AURORA_CARE_OPTION_MODAL_DESCRIPTIONS[AuroraCareOption.MEDICATION_CONSULTATION]),
        programPointsHeaderText: formatMessage({
          defaultMessage: 'We’re by your side throughout the entire process:',
          description: 'Header of section which explains what to expect from the medication consultation program',
        }),
        programPoints: [
          {
            icon: 'ProgressIcon',
            description: formatMessage({
              defaultMessage: 'Symptom check-ins to mitigate side effects',
              description: 'List item in description of the medication consultation program',
            }),
          },
          {
            icon: 'BookSessionsIcon',
            description: formatMessage({
              defaultMessage: 'Easy access to refills and follow-ups',
              description: 'List item in description of the medication consultation program',
            }),
          },
          ...(!isDAMedsOnly
            ? [
                {
                  icon: 'InfoIcon',
                  description: isDirectAccessMedsSupported
                    ? formatMessage(MEDICATION_CONSULTATION_CONTENT.INFO_TEXT, {
                        link: (chunks: string) => (
                          <Link
                            text={chunks}
                            onPress={onContinuePress}
                            underline
                            testID={tID(`AuroraCareOption-medicationConsultation-continue`)}
                            medium
                          />
                        ),
                        hideContinueLink: onContinuePress === noop ? 'yes' : 'other',
                      })
                    : formatMessage(MEDICATION_CONSULTATION_CONTENT.INFO_TEXT_NO_DIRECT_ACCESS),
                },
              ]
            : []),
        ],
        ctaText: formatMessage(MODAL_CTA_TEXT.BOOK_CONSULTATION),
        ...createModalCallbacks(AuroraCareOption.MEDICATION_CONSULTATION),
      },
      [AuroraCareOption.MEDICATION_CONSULTATION_CHILDREN]: {
        header: formatMessage(MEDICATION_CONSULTATION_CONTENT.HEADER_FOR_DEPENDENTS, {
          dependentType: formatMessage(MEDICATION_CONSULTATION_CONTENT.CHILDREN),
        }),
        description: formatMessage(MEDICATION_CONSULTATION_CONTENT.DESCRIPTION_FOR_DEPENDENTS, {
          dependentType: formatMessage(MEDICATION_CONSULTATION_CONTENT.CHILD),
        }),
        programPoints: MEDICATION_CONSULTATION_DEPENDENTS_PROGRAM_POINTS,
        ctaText: formatMessage(MODAL_CTA_TEXT.BOOK_CONSULTATION),
        ...createModalCallbacks(AuroraCareOption.MEDICATION_CONSULTATION_CHILDREN),
      },
      [AuroraCareOption.MEDICATION_CONSULTATION_TEENS]: {
        header: formatMessage(MEDICATION_CONSULTATION_CONTENT.HEADER_FOR_DEPENDENTS, {
          dependentType: formatMessage(MEDICATION_CONSULTATION_CONTENT.TEENS),
        }),
        description: formatMessage(MEDICATION_CONSULTATION_CONTENT.DESCRIPTION_FOR_DEPENDENTS, {
          dependentType: formatMessage(MEDICATION_CONSULTATION_CONTENT.TEEN),
        }),
        programPoints: MEDICATION_CONSULTATION_DEPENDENTS_PROGRAM_POINTS,
        ctaText: formatMessage(MODAL_CTA_TEXT.BOOK_CONSULTATION),
        ...createModalCallbacks(AuroraCareOption.MEDICATION_CONSULTATION_TEENS),
      },
      [AuroraCareOption.AUD_SUD]: {
        header: formatMessage(AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.AUD_SUD]),
        description: formatMessage(AURORA_CARE_OPTION_MODAL_DESCRIPTIONS[AuroraCareOption.AUD_SUD]),
        programPoints: [
          {
            icon: 'BookSessionsIcon',
            description: formatMessage({
              defaultMessage: '50-minute sessions on your schedule',
              description: 'List item in description of the alcohol or substance use treatment program',
            }),
          },
          {
            icon: 'PracticeSkillsIcon',
            description: formatMessage({
              defaultMessage: 'Resources and activities between sessions',
              description: 'List item in description of the alcohol or substance use treatment program',
            }),
          },
          {
            icon: 'ProgressIcon',
            description: formatMessage({
              defaultMessage: 'Track progress with your therapist',
              description: 'List item in description of the alcohol or substance use treatment program',
            }),
          },
          {
            icon: 'GroupSessionIcon',
            description: formatMessage({
              defaultMessage: 'Optional group sessions led by a specialist',
              description: 'List item in description of the alcohol or substance use treatment program',
            }),
          },
        ],
        ctaText: formatMessage(MODAL_CTA_TEXT.FIND_THERAPIST),
        ...createModalCallbacks(AuroraCareOption.AUD_SUD),
      },
      [AuroraCareOption.DBT]: {
        header: formatMessage(AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.DBT]),
        description: formatMessage(AURORA_CARE_OPTION_MODAL_DESCRIPTIONS[AuroraCareOption.DBT]),
        programPoints: [
          {
            icon: 'LiveVideoModalityIcon',
            descriptionEyebrow: formatMessage(HOW_IT_WORKS_CONTENT_SHARED.SESSION_OPTIONS),
            description: formatMessage(HOW_IT_WORKS_CONTENT_SHARED.SESSION_OPTIONS_BY_VIDEO),
          },
          {
            ...SHARED_PROGRAM_POINTS.TREATMENT_LENGTH,
            description: formatMessage({
              defaultMessage: '4 months',
              description: 'Treatment length for the self-harm support program',
            }),
          },
        ],
        ctaText: formatMessage(MODAL_CTA_TEXT.SCHEDULE_CALL),
        ...createModalCallbacks(AuroraCareOption.DBT),
      },
      [AuroraCareOption.IOP]: {
        header: formatMessage(AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.IOP]),
        description: formatMessage(AURORA_CARE_OPTION_MODAL_DESCRIPTIONS[AuroraCareOption.IOP]),
        programPoints: [
          {
            ...SHARED_PROGRAM_POINTS.SESSION_OPTIONS,
            description: formatMessage(HOW_IT_WORKS_CONTENT_SHARED.SESSION_OPTIONS_BOTH),
          },
          {
            ...SHARED_PROGRAM_POINTS.TREATMENT_LENGTH,
            description: formatMessage({
              defaultMessage: '4 to 8 weeks',
              description: 'Treatment length for the Intensive Outpatient Program',
            }),
          },
          SHARED_PROGRAM_POINTS.LOCATION_HOME,
          SHARED_PROGRAM_POINTS.COMPLEX_CARE_SPECIALIST,
        ],
        ctaText: formatMessage(MODAL_CTA_TEXT.SCHEDULE_CALL),
        ...createModalCallbacks(AuroraCareOption.IOP),
      },
      [AuroraCareOption.PHP]: {
        header: formatMessage(AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.PHP]),
        description: formatMessage(AURORA_CARE_OPTION_MODAL_DESCRIPTIONS[AuroraCareOption.PHP]),
        programPoints: [
          {
            ...SHARED_PROGRAM_POINTS.SESSION_OPTIONS,
            description: formatMessage(HOW_IT_WORKS_CONTENT_SHARED.SESSION_OPTIONS_BOTH),
          },
          {
            ...SHARED_PROGRAM_POINTS.TREATMENT_LENGTH,
            description: formatMessage({
              defaultMessage: '6 to 8 weeks',
              description: 'Treatment length for the Partial Hospitalization Program',
            }),
          },
          SHARED_PROGRAM_POINTS.LOCATION_HOME,
          SHARED_PROGRAM_POINTS.COMPLEX_CARE_SPECIALIST,
        ],
        ctaText: formatMessage(MODAL_CTA_TEXT.SCHEDULE_CALL),
        ...createModalCallbacks(AuroraCareOption.PHP),
      },
      [AuroraCareOption.RESIDENTIAL_TREATMENT]: {
        header: formatMessage(AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.RESIDENTIAL_TREATMENT]),
        description: formatMessage(AURORA_CARE_OPTION_MODAL_DESCRIPTIONS[AuroraCareOption.RESIDENTIAL_TREATMENT]),
        programPoints: [
          {
            ...SHARED_PROGRAM_POINTS.SESSION_OPTIONS,
            description: formatMessage(HOW_IT_WORKS_CONTENT_SHARED.SESSION_OPTIONS_IN_PERSON),
          },
          {
            ...SHARED_PROGRAM_POINTS.TREATMENT_LENGTH,
            description: formatMessage({
              defaultMessage: '1 to 3 months',
              description: 'Treatment length for the Residential Treatment Program',
            }),
          },
          {
            icon: 'OnsiteIcon',
            descriptionEyebrow: formatMessage(HOW_IT_WORKS_CONTENT_SHARED.LOCATION),
            description: formatMessage({
              defaultMessage: 'Facility',
              description: 'Location for the Residential Treatment Program',
            }),
          },
          SHARED_PROGRAM_POINTS.COMPLEX_CARE_SPECIALIST,
        ],
        ctaText: formatMessage(MODAL_CTA_TEXT.SCHEDULE_CALL),
        ...createModalCallbacks(AuroraCareOption.RESIDENTIAL_TREATMENT),
      },
      [AuroraCareOption.INPATIENT_HOSPITALIZATION]: {
        header: formatMessage(AURORA_CARE_OPTIONS_HEADERS[AuroraCareOption.INPATIENT_HOSPITALIZATION]),
        description: formatMessage(AURORA_CARE_OPTION_MODAL_DESCRIPTIONS[AuroraCareOption.INPATIENT_HOSPITALIZATION]),
        programPoints: [
          {
            ...SHARED_PROGRAM_POINTS.SESSION_OPTIONS,
            description: formatMessage(HOW_IT_WORKS_CONTENT_SHARED.SESSION_OPTIONS_IN_PERSON),
          },
          {
            ...SHARED_PROGRAM_POINTS.TREATMENT_LENGTH,
            description: formatMessage({
              defaultMessage: 'Typically 3 to 7 days',
              description: 'Treatment length for the Inpatient Hospitalization Program',
            }),
          },
          {
            icon: 'OnsiteIcon',
            descriptionEyebrow: formatMessage(HOW_IT_WORKS_CONTENT_SHARED.LOCATION),
            description: formatMessage({
              defaultMessage: 'Hospital',
              description: 'Location for the Inpatient Hospitalization Program',
            }),
          },
          SHARED_PROGRAM_POINTS.COMPLEX_CARE_SPECIALIST,
        ],
        ctaText: formatMessage(MODAL_CTA_TEXT.SCHEDULE_CALL),
        ...createModalCallbacks(AuroraCareOption.INPATIENT_HOSPITALIZATION),
      },
    }
  }, [
    MEDICATION_CONSULTATION_DEPENDENTS_PROGRAM_POINTS,
    SHARED_PROGRAM_POINTS,
    createModalCallbacks,
    formatMessage,
    isDAMedsOnly,
    isDirectAccessMedsSupported,
    onContinuePress,
  ])
}
